import React, { useState } from "react";
import type { LedgerTransaction } from "../../../hooks/useMyAccountData";

interface CreditHistoryModalProps {
  ledger: LedgerTransaction[];
  onClose: () => void;
}

const formatType = (type: string): string => {
  const map: Record<string, string> = {
    ENTITY_LOOKUP: "Entity Lookup",
    KYC_IDENTITY_CHECK: "Identity Check (KYC)",
    PEP_TFS_SCREENING: "PEP / TFS Screening",
    CREDIT_PURCHASE: "Credit Purchase",
    CREDIT_ALLOCATION: "Credit Allocation",
    RECONCILIATION: "Reconciliation",
  };
  return map[type] || type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatDescription = (desc: string): string => {
  // Strip the raw type prefix (e.g. "KYC_IDENTITY_CHECK: ") and case reference
  const cleaned = desc
    .replace(/^[A-Z_]+:\s*/, "")
    .replace(/\s*\[case=[^\]]+\]/, "");
  return cleaned || desc;
};

const downloadCSV = (ledger: LedgerTransaction[]) => {
  const headers = ["Date", "Type", "Description", "Amount", "Balance After", "Reference"];
  const rows = ledger.map((tx) => [
    new Date(tx.processedAt).toLocaleString("en-AU"),
    formatType(tx.type),
    `"${tx.description.replace(/"/g, '""')}"`,
    tx.amount.toString(),
    tx.balanceAfter.toString(),
    tx.referenceId || "",
  ]);

  const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `credit-history-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const CreditHistoryModal: React.FC<CreditHistoryModalProps> = ({
  ledger,
  onClose,
}) => {
  const [filter, setFilter] = useState<"all" | "credit" | "debit">("all");

  const filtered = ledger.filter((tx) => {
    if (filter === "credit") return tx.amount > 0;
    if (filter === "debit") return tx.amount < 0;
    return true;
  });

  const totalCredits = ledger.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const totalDebits = ledger.filter((t) => t.amount < 0).reduce((s, t) => s + t.amount, 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content large"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 780 }}
      >
        {/* Header */}
        <div className="modal-header" style={{ borderBottom: "1px solid #e5e7eb", paddingBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111" }}>
              📊 Credit History
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6b7280" }}>
              {ledger.length} transaction{ledger.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        {/* Summary Cards */}
        {ledger.length > 0 && (
          <div style={{ display: "flex", gap: 12, padding: "16px 24px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
            <div style={{ flex: 1, background: "#fff", borderRadius: 10, padding: "12px 16px", border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Credits Added</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#16a34a", marginTop: 2 }}>+{totalCredits}</div>
            </div>
            <div style={{ flex: 1, background: "#fff", borderRadius: 10, padding: "12px 16px", border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Credits Used</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#dc2626", marginTop: 2 }}>{totalDebits}</div>
            </div>
            <div style={{ flex: 1, background: "#fff", borderRadius: 10, padding: "12px 16px", border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Current Balance</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#1d4ed8", marginTop: 2 }}>
                {ledger.length > 0 ? ledger[0].balanceAfter : 0}
              </div>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 24px", borderBottom: "1px solid #f3f4f6" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {(["all", "credit", "debit"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: "5px 12px",
                  fontSize: 12,
                  fontWeight: filter === f ? 600 : 400,
                  borderRadius: 6,
                  border: `1px solid ${filter === f ? "#2563eb" : "#e5e7eb"}`,
                  background: filter === f ? "#eff6ff" : "#fff",
                  color: filter === f ? "#2563eb" : "#6b7280",
                  cursor: "pointer",
                }}
              >
                {f === "all" ? "All" : f === "credit" ? "Credits" : "Debits"}
              </button>
            ))}
          </div>
          <button
            onClick={() => downloadCSV(filtered)}
            style={{
              padding: "5px 14px",
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#374151",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            ⬇ Download CSV
          </button>
        </div>

        {/* Table */}
        <div className="modal-body" style={{ padding: 0, maxHeight: 400, overflowY: "auto" }}>
          {filtered.length === 0 ? (
            <p style={{ color: "#888", textAlign: "center", padding: "32px 0" }}>
              No transactions found.
            </p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9fafb", position: "sticky", top: 0 }}>
                  <th style={{ padding: "10px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e5e7eb" }}>Date</th>
                  <th style={{ padding: "10px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e5e7eb" }}>Type</th>
                  <th style={{ padding: "10px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e5e7eb" }}>Description</th>
                  <th style={{ padding: "10px 16px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e5e7eb" }}>Amount</th>
                  <th style={{ padding: "10px 16px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e5e7eb" }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((tx, i) => (
                  <tr
                    key={tx.transactionId}
                    style={{
                      borderBottom: "1px solid #f3f4f6",
                      background: i % 2 === 0 ? "#fff" : "#fafbfc",
                    }}
                  >
                    <td style={{ padding: "10px 16px", whiteSpace: "nowrap", color: "#6b7280", fontSize: 12 }}>
                      {new Date(tx.processedAt).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                      <br />
                      <span style={{ fontSize: 10, color: "#9ca3af" }}>
                        {new Date(tx.processedAt).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </td>
                    <td style={{ padding: "10px 16px" }}>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 4,
                          background: tx.amount < 0 ? "#fef2f2" : "#f0fdf4",
                          color: tx.amount < 0 ? "#991b1b" : "#166534",
                        }}
                      >
                        {formatType(tx.type)}
                      </span>
                    </td>
                    <td style={{ padding: "10px 16px", color: "#374151", fontSize: 12.5, maxWidth: 260 }}>
                      {formatDescription(tx.description)}
                    </td>
                    <td
                      style={{
                        padding: "10px 16px",
                        textAlign: "right",
                        fontWeight: 700,
                        fontSize: 13,
                        color: tx.amount < 0 ? "#dc2626" : "#16a34a",
                      }}
                    >
                      {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                    </td>
                    <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 600, color: "#111", fontSize: 13 }}>
                      {tx.balanceAfter}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ borderTop: "1px solid #e5e7eb", padding: "12px 24px", display: "flex", justifyContent: "flex-end" }}>
          <button className="action-button primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreditHistoryModal;
